import { z } from "zod";
/**
 * Score de reincidência — a ordenação da aba de erradas (RN-45).
 *
 * ## Por que não é "mais errados primeiro"
 *
 * A RN-12 ordenava por contagem bruta de erros. Com poucas amostras isso ordena
 * ruído: um item errado 1 vez em 1 tentativa fica atrás de um errado 2 vezes em
 * 20, quando o primeiro é o que se sabe menos sobre. E um item errado 3 vezes em
 * 3 empata com um errado 3 vezes em 30, que são situações opostas.
 *
 * A média bayesiana resolve as duas: ela é a taxa de erro **puxada para uma
 * expectativa a priori** com força proporcional à falta de amostra.
 *
 * ```
 * score = (erros + alfa) / (tentativas + alfa + beta)
 * ```
 *
 * `alfa` é o peso do erro na expectativa e `beta` o do acerto. Com o padrão de
 * `1` e `3`, um item sem histórico nenhum vale `1/4 = 0,25`: a expectativa é de
 * que se erre um quarto das vezes. Cada tentativa real desloca o score na
 * direção do que aconteceu, e quanto mais tentativas, menos a expectativa pesa.
 *
 * **`alfa` e `beta` são configuração da trilha**, nunca constante daqui
 * (invariante 4). Trilha de decoreba e trilha de raciocínio não têm a mesma
 * expectativa de erro, e o número que decide a ordem de estudo é regra de
 * negócio.
 *
 * ## O que o score não faz
 *
 * Ele **não toca o SM-2 nem a fila de revisão do dia**. Ordena a aba de erradas
 * e nada mais. São duas perguntas diferentes: o SM-2 responde *quando o item
 * volta*, e isto responde *em que ordem o que já está pendente aparece*. ADR 014.
 */
/** Os dois parâmetros da média, vindos da trilha (RN-45, invariante 4). */
export const zParametrosDeReincidencia = z.object({
    /**
     * Peso do erro na expectativa a priori. Positivo: `alfa` zero faria um item
     * sem histórico valer 0 e nunca subir, que é o oposto do que a fila quer.
     */
    alfa: z.number().positive(),
    /** Peso do acerto na expectativa a priori. */
    beta: z.number().positive(),
});
export const REINCIDENCIA_PADRAO = { alfa: 1, beta: 3 };
/**
 * A conta, isolada, para poder ser mostrada na tela (RN-32: mostrar a conta,
 * não só o resultado).
 *
 * `tentativas` menor que `erros` é dado inconsistente, e a conta não tenta
 * consertar: ela devolveria um score acima de 1, que é visivelmente errado na
 * tela — melhor do que um número plausível escondendo um defeito de gravação.
 */
export function scoreDeReincidencia(a, p) {
    return (a.erros + p.alfa) / (a.tentativas + p.alfa + p.beta);
}
/**
 * Ordena a fila de erradas: **score decrescente, e o parado há mais tempo
 * primeiro no empate** (RN-45).
 *
 * O desempate não é enfeite. Dois itens com a mesma taxa de erro são igualmente
 * frágeis, e entre eles o que interessa é o que está esfriando há mais tempo —
 * sem isso a fila devolveria sempre os mesmos itens na mesma ordem e a cauda
 * nunca seria alcançada.
 *
 * O `itemId` desempata o desempate. Não é zelo: sem um terceiro critério
 * estável, dois itens idênticos trocariam de lugar entre duas leituras da mesma
 * lista, dependendo da ordem em que o banco os devolveu.
 */
export function ordenarPorReincidencia(amostras, p) {
    return amostras
        .map((a) => ({ ...a, score: scoreDeReincidencia(a, p) }))
        .sort((x, y) => y.score - x.score || y.diasParado - x.diasParado || x.itemId.localeCompare(y.itemId));
}
/**
 * Dias inteiros entre duas datas UTC.
 *
 * Dia **civil do usuário** é o que vale para ofensiva e heatmap (invariante 16),
 * mas aqui a pergunta é outra: "há quanto tempo isto não é tocado". Isso é
 * duração, não calendário — um item respondido às 23h de ontem está parado há
 * poucas horas, e contá-lo como "1 dia parado" o faria subir na fila por causa
 * da meia-noite, não por causa do esquecimento.
 */
export function diasParados(ultimaTentativaUtc, agoraUtc) {
    const ms = Date.parse(agoraUtc) - Date.parse(ultimaTentativaUtc);
    return ms <= 0 ? 0 : Math.floor(ms / 86_400_000);
}
//# sourceMappingURL=reincidencia.js.map